import styled, { css } from 'styled-components'
import { Button } from '@components/Button'
import { Textarea } from '@components/Field'
import { black, border1, border2, darkOrange, orange, surface2, textMuted, white } from '@/theme/colors'
import { motion, radii, shadows } from '@/theme/tokens'
import { fontSizes, leading, text } from '@/theme/typography'
import { chatAccent } from './ChatMessage.styles'

/** Past a few lines the field stops growing and scrolls its own text. */
export const FIELD_MAX_HEIGHT = 120

/** The composer's panel, under the stream. */
export const ComposerPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 10px 0 12px;
	background: ${surface2};
	border-top: 1px solid ${border1};
`

const row = css`
	padding: 0 16px;
`

export const ReplyBox = styled.div`
	margin: 0 16px;
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 6px 8px 10px;
	border-radius: ${radii.inner};
	border: 1px solid ${border1};
	background: ${white};
`

export const ReplyBar = styled.span<{ $quotesMine: boolean }>`
	align-self: stretch;
	width: 3px;
	border-radius: ${radii.round};
	background: ${({ $quotesMine }) => chatAccent($quotesMine)};
`

export const ReplyText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	flex: 1;
`

export const ReplyTitle = styled.span<{ $quotesMine: boolean }>`
	${text.caption}
	color: ${({ $quotesMine }) => chatAccent($quotesMine)};
`

export const ReplySnippet = styled.span<{ $removed: boolean }>`
	${text.caption}
	font-weight: 400;
	color: ${textMuted};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	${({ $removed }) => $removed && 'font-style: italic;'}
`

/** What's attached, in one row that scrolls sideways when it doesn't fit. */
export const Attachments = styled.div`
	${row}
	display: flex;
	align-items: center;
	gap: 8px;
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

export const AttachedPhoto = styled.span`
	position: relative;
	flex-shrink: 0;
	width: 56px;
	height: 56px;
	border-radius: ${radii.inner};
	overflow: hidden;
	background: ${white};

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		display: block;
	}
`

export const RemovePhotoButton = styled(Button).attrs({ variant: 'icon' })`
	position: absolute;
	top: 2px;
	right: 2px;
	width: 22px;
	height: 22px;
	border-radius: ${radii.round};
	background: ${black}99;
	color: ${white};

	&:hover:not(:disabled) {
		background: ${black}cc;
		color: ${white};
	}
`

export const LinkPreview = styled.div`
	margin: 0 16px;
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 4px 8px 8px;
	border-radius: ${radii.inner};
	border: 1px solid ${border1};
	background: ${white};
`

export const LinkPreviewText = styled.span`
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	flex: 1;
`

export const LinkPreviewTitle = styled.span`
	${text.labelSm}
	color: ${black};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const LinkPreviewSite = styled.span`
	${text.caption}
	font-weight: 400;
	color: ${textMuted};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

/** The camera, then a chip for each kind of thing a Message can attach. */
export const ChipRow = styled.div`
	${row}
	display: flex;
	align-items: center;
	gap: 8px;
	overflow-x: auto;
	scrollbar-width: none;

	&::-webkit-scrollbar {
		display: none;
	}
`

export const CameraButton = styled(Button).attrs({ variant: 'icon' })`
	flex-shrink: 0;
	color: ${darkOrange};
`

export const Chip = styled(Button).attrs({ variant: 'secondary', size: 'sm' })`
	flex-shrink: 0;
	color: ${orange};
	border-color: ${border1};
`

export const FieldRow = styled.div`
	${row}
	display: flex;
	align-items: flex-end;
	gap: 8px;
`

/** Holds the field over its mirror, which draws the tagged members' `@Name`s in colour. */
export const FieldWrap = styled.div`
	position: relative;
	flex: 1;
	min-width: 0;
	border-radius: ${radii.band};
	border: 1px solid ${border2};
	background: ${white};
	transition:
		border-color ${motion.base},
		box-shadow ${motion.base};

	&:focus-within {
		border-color: ${orange};
		box-shadow: ${shadows.focus};
	}
`

const fieldText = css`
	${text.body}
	padding: 9px 16px;
	margin: 0;
	white-space: pre-wrap;
	overflow-wrap: break-word;
	word-break: break-word;
`

/**
 * The text as typed, drawn under the field: plain, with each mention in orange. It sits in
 * the flow, so the field over it grows with its text up to a few lines, then both scroll.
 */
export const FieldMirror = styled.div`
	${fieldText}
	position: relative;
	max-height: ${FIELD_MAX_HEIGHT}px;
	/* Scrolls (and so wraps) exactly as the field does; the field keeps it in step. */
	overflow-y: auto;
	scrollbar-gutter: stable;
	color: ${black};
	pointer-events: none;
	border: 1px solid transparent;
`

export const MirrorMention = styled.span`
	color: ${orange};
`

/** The field itself: its text is drawn by the mirror under it, its caret and selection by it. */
export const ComposerField = styled(Textarea)`
	${fieldText}
	position: absolute;
	inset: 0;
	display: block;
	width: 100%;
	height: 100%;
	min-height: 0;
	line-height: ${leading.body};
	font-size: ${fontSizes.body};
	resize: none;
	border: 1px solid transparent;
	border-radius: ${radii.band};
	background: transparent;
	color: transparent;
	caret-color: ${black};
	overflow-y: auto;
	scrollbar-gutter: stable;

	&::placeholder {
		color: ${textMuted};
	}

	&:focus {
		border-color: transparent;
		box-shadow: none;
	}
`

export const SendButton = styled(Button).attrs({ variant: 'primary' })`
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	padding: 0;
	border-radius: ${radii.round};
`
